import * as React from 'react';

// @ts-expect-error
import {FieldProps} from 'redux-form';

import type {DisplayOptionEntity} from 'nutshell-core/utils';

import {Avatar} from 'shells/avatar/avatar';
import {IconAvatar} from 'shells/avatar/icon-avatar';
import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {CompanyIcon} from 'shells/icon/svg/company-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TrashIcon} from 'shells/icon/svg/trash-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {EntityPageAddItemButton} from 'shells/lead-sidebar/entity-page-add-item-button';
import {AccountCard} from 'shells/peep-cards/account-card/account-card';
import {AccountCardCreateable} from 'shells/peep-cards/account-card/account-card-createable/account-card-createable';
import type {AccountInput} from 'shells/peep-cards/account-card/types';

import {useValidatedAvatarUrls} from '../../../../hooks/use-validated-avatar-url';

import {accountInputToDisplayOptionEntity} from './helpers';
import {getPotentialAccounts} from './rich-account-select-helpers';

const NEW_ACCOUNT_MAGIC_ID = 'new-account-magic-id';

type Props = FieldProps;

export function RichAccountSelect(props: Props) {
    const {input} = props;

    const [isEditingAccountId, setIsEditingAccountId] = React.useState<string | null>(null);
    const validatedAvatarUrls = useValidatedAvatarUrls(input.value || []);

    const alreadyAddedAccountIds = React.useMemo(
        // @ts-expect-error
        () => (Array.isArray(input.value) ? input.value.map((a) => a.id) : []),
        [input.value]
    );

    const handleCreateAccount = (accountInput: AccountInput) => {
        input.onChange([
            ...input.value,
            accountInputToDisplayOptionEntity(
                accountInput,
                true,
                String(Date.now()) + String(Math.random())
            ),
        ]);
        setIsEditingAccountId(null);
    };

    const handleUpdateAccount = (accountInput: AccountInput) => {
        const updatedAccount = accountInputToDisplayOptionEntity(accountInput, true);

        input.onChange(
            // @ts-expect-error
            input.value.map((account) =>
                account.id === isEditingAccountId ? {id: account.id, ...updatedAccount} : account
            )
        );
        setIsEditingAccountId(null);
    };

    const handleSelectAccount = (account: DisplayOptionEntity) => {
        input.onChange([...input.value, account]);
        setIsEditingAccountId(null);
    };

    const handleRemoveAccount = (id: string) => {
        // @ts-expect-error
        input.onChange(input.value.filter((a) => a.id !== id));
        setIsEditingAccountId(null);
    };

    return (
        <StackLegacy spacing={12}>
            {input.value &&
                // @ts-expect-error
                input.value.map((account) => {
                    const avatarUrlValidated = validatedAvatarUrls[account.id];

                    if (account.id === isEditingAccountId) {
                        return (
                            <AccountCardCreateable
                                key={account.id}
                                onSelectAccount={handleSelectAccount}
                                onSubmit={handleUpdateAccount}
                                onClose={() => setIsEditingAccountId(null)}
                                temporaryAccount={{
                                    id: account.id,
                                    name: account.value,
                                    description: account.description,
                                    address: account.tertiaryInfo,
                                }}
                            />
                        );
                    }

                    return (
                        <AccountCard
                            key={account.id}
                            name={account.value}
                            description={account.description}
                            location={account.tertiaryInfo}
                            actionMenuOptions={[
                                {
                                    label: 'Remove',
                                    icon: <TrashIcon size={12} wrapWithDiv={false} />,
                                    handleSelect: () => handleRemoveAccount(account.id),
                                },
                            ]}
                            tertiaryProps={
                                account.isNew
                                    ? {
                                          type: 'text-button',
                                          text: 'Edit',
                                          onClick: () => setIsEditingAccountId(account.id),
                                      }
                                    : undefined
                            }
                            accountUrl={null}
                            icon={
                                avatarUrlValidated ? (
                                    <Avatar
                                        avatarUrl={avatarUrlValidated}
                                        size={32}
                                        circular={false}
                                        hasBorderRadius={true}
                                    />
                                ) : (
                                    <IconAvatar
                                        icon={CompanyIcon}
                                        avatarSize={32}
                                        iconSize={16}
                                        iconColor={colors.plum}
                                        backgroundColor={colors.plumXlt}
                                        shape='square'
                                    />
                                )
                            }
                        />
                    );
                })}
            {isEditingAccountId === NEW_ACCOUNT_MAGIC_ID && (
                <AccountCardCreateable
                    onSelectAccount={handleSelectAccount}
                    onSubmit={handleCreateAccount}
                    onClose={() => setIsEditingAccountId(null)}
                    onSearchPotentialAccounts={(searchInput: AccountInput) =>
                        getPotentialAccounts({
                            accountInput: searchInput,
                            alreadyAddedAccounts: alreadyAddedAccountIds,
                        })
                    }
                />
            )}
            {!isEditingAccountId && (
                <EntityPageAddItemButton
                    sectionEntity='accounts'
                    parentItemText='a company'
                    onAddItem={() => {
                        setIsEditingAccountId(NEW_ACCOUNT_MAGIC_ID);
                    }}
                    omitEntityName={true}
                />
            )}
        </StackLegacy>
    );
}
